/**
 * Pricing Engine
 *
 * Pure calculation functions for ticket pricing with discount support.
 * Single source of truth for all pricing calculations across the application.
 *
 * Lives in `shared/` (moved from `server/services/pricingEngine.ts`, which
 * re-exports this module) so the public booking client computes the EXACT
 * same per-ticket totals the server recomputes authoritatively on the batch
 * checkout endpoint. Any drift between the two would reject paid checkouts,
 * so there must be exactly one implementation.
 *
 * Key Features:
 * - Pure functions (deterministic, no side effects)
 * - Supports percentage and fixed amount discounts
 * - Caps discounts at base price (prevents negative prices)
 * - Rounds to 2 decimal places for currency accuracy
 * - Type-safe with full TypeScript support
 */

export interface DiscountDefinition {
  id: string;
  valueType: 'percent' | 'amount';
  value: number;
}

export interface TicketPricingResult {
  basePrice: number;
  discountAmount: number;
  finalPrice: number;
  discountId?: string;
}

/**
 * Calculate pricing for a single ticket with optional discount
 * Pure function - no side effects, no database access
 *
 * @param basePrice - Ticket price before discount (one-way or round-trip unit price)
 * @param discount - Discount definition or null for no discount
 * @param tripType - Type of trip (affects base price selection)
 * @returns Pricing breakdown with discount applied
 *
 * @example
 * // No discount
 * calculateTicketPrice(100, null, 'one-way')
 * // Returns: { basePrice: 100, discountAmount: 0, finalPrice: 100 }
 *
 * @example
 * // 20% discount
 * calculateTicketPrice(100, { id: 'child', valueType: 'percent', value: 20 }, 'one-way')
 * // Returns: { basePrice: 100, discountAmount: 20, finalPrice: 80, discountId: 'child' }
 */
export function calculateTicketPrice(
  basePrice: number,
  discount: DiscountDefinition | null,
  // Underscore-prefixed: callers select the one-way vs round-trip base fare
  // BEFORE calling; the parameter is kept for call-site clarity but unused
  // (client-public compiles this file with noUnusedParameters).
  _tripType: 'one-way' | 'round-trip'
): TicketPricingResult {
  // Validation
  if (basePrice < 0) {
    throw new Error('Base price cannot be negative');
  }

  // No discount case
  if (!discount) {
    return {
      basePrice,
      discountAmount: 0,
      finalPrice: basePrice
    };
  }

  // Calculate discount amount
  let discountAmount = 0;
  if (discount.valueType === 'percent') {
    // Round to 2 decimal places: basePrice × value / 100
    discountAmount = Math.round((basePrice * discount.value / 100) * 100) / 100;
  } else {
    // Fixed amount: cap at base price (can't discount more than ticket costs)
    discountAmount = Math.min(discount.value, basePrice);
  }

  // Final price cannot go below zero
  const finalPrice = Math.max(basePrice - discountAmount, 0);

  return {
    basePrice,
    discountAmount,
    finalPrice,
    discountId: discount.id
  };
}

/**
 * Calculate pricing for group booking with per-passenger discounts
 *
 * @param passengers - Array of passenger discount selections
 * @param basePrice - Base ticket price per passenger
 * @param discounts - Map of available discounts by ID
 * @param tripType - Type of trip
 * @returns Breakdown per passenger and total amount
 *
 * @example
 * const discounts = new Map([
 *   ['child', { id: 'child', valueType: 'percent', value: 20 }],
 *   ['senior', { id: 'senior', valueType: 'percent', value: 15 }]
 * ]);
 *
 * const passengers = [
 *   { discountId: null },      // Adult - full price
 *   { discountId: 'child' },   // Child - 20% off
 *   { discountId: 'senior' }   // Senior - 15% off
 * ];
 *
 * calculateGroupPricing(passengers, 100, discounts, 'one-way')
 * // Returns: {
 * //   breakdown: [
 * //     { basePrice: 100, discountAmount: 0, finalPrice: 100 },
 * //     { basePrice: 100, discountAmount: 20, finalPrice: 80, discountId: 'child' },
 * //     { basePrice: 100, discountAmount: 15, finalPrice: 85, discountId: 'senior' }
 * //   ],
 * //   totalAmount: 265
 * // }
 */
export function calculateGroupPricing(
  passengers: Array<{ discountId: string | null }>,
  basePrice: number,
  discounts: Map<string, DiscountDefinition>,
  tripType: 'one-way' | 'round-trip'
): {
  breakdown: TicketPricingResult[];
  totalAmount: number;
} {
  const breakdown = passengers.map(passenger => {
    const discount = passenger.discountId
      ? discounts.get(passenger.discountId) || null
      : null;
    return calculateTicketPrice(basePrice, discount, tripType);
  });

  const totalAmount = breakdown.reduce((sum, item) => sum + item.finalPrice, 0);

  return { breakdown, totalAmount };
}
